import React from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';

import {
  ExtendedBox,
  Body,
  Link,
  ExtendedFlex,
  Formatters,
} from '../../coreui';

import DashboardTableCell from './DashboardTableCell';
import DashboardSection from './DashboardSection';
import HeaderComponent from './TableHeader';
import StyledTable from './StyledTable';

const columnsActive = (data) => {
  const sumAmount = data.ios.reduce(
    (accum, item) => (accum + parseFloat(item.totalInvestmentAmount, 10)), 0.00,
  );
  const distributionsSumAmount = data.ios.reduce(
    (accum, item) => (accum + parseFloat(item.totalDistributionsPaid, 10)), 0.00,
  );

  return [
    {
      Header: () => <HeaderComponent>Investment</HeaderComponent>,
      accessor: 'title',
      minWidth: 350,
      Cell: DashboardTableCell(),
      Footer: () => <HeaderComponent>Totals</HeaderComponent>,
    },
    {
      width: 100,
      Header: () => <HeaderComponent>Type</HeaderComponent>,
      accessor: 'type',
      Cell: DashboardTableCell(),
    },
    {
      width: 170,
      Header: () => <HeaderComponent>Date</HeaderComponent>,
      accessor: 'date',
      Cell: DashboardTableCell(Formatters.MMDDYYYY),
    },
    {
      minWidth: 250,
      Header: () => <HeaderComponent>Amount</HeaderComponent>,
      accessor: 'totalInvestmentAmount',
      Cell: DashboardTableCell(Formatters.DOLLARS_DECIMALS),
      Footer: () => {
        return (
          <HeaderComponent>
            {numeral(sumAmount).format(Formatters.DOLLARS_DECIMALS)}
          </HeaderComponent>
        );
      },
    },
    {
      minWidth: 250,
      Header: () => <HeaderComponent>Cash Distributions Paid</HeaderComponent>,
      accessor: 'totalDistributionsPaid',
      Cell: DashboardTableCell(Formatters.DOLLARS_DECIMALS),
      Footer: () => {
        return (
          <HeaderComponent>
            {numeral(distributionsSumAmount).format(Formatters.DOLLARS_DECIMALS)}
          </HeaderComponent>
        );
      },
    },
    {
      minWidth: 250,
      Header: <HeaderComponent>Next Distribution Date</HeaderComponent>,
      Cell: DashboardTableCell(Formatters.MMYYYY),
      accessor: 'nextDistribution',
    },
  ];
};

const columnsCompleted = (data) => {
  const sumInvested = data.ios.reduce((accum, item) => (accum + parseFloat(item.amount, 10)), 0.00);
  const sumReturned = data.ios.reduce(
    (accum, item) => (accum + parseFloat(item.totalReturned, 10)),
    0.00,
  );

  return [
    {
      Header: 'Investment',
      accessor: 'title',
      Footer: 'Totals',
    },
    {
      Header: 'Type',
      accessor: 'type',
    },
    {
      Header: 'Close of Escrow Date',
      accessor: 'escrowDate',
    },
    {
      Header: 'Maturity Date',
      accessor: 'maturityDate',
    },
    {
      Header: 'Investment Amount',
      accessor: 'amount',
      Cell: props => numeral(props.value).format(Formatters.DOLLARS_DECIMALS),
      Footer: numeral(sumInvested).format(Formatters.DOLLARS_DECIMALS),
    },
    {
      Header: 'Total Returned',
      accessor: 'totalReturned',
      Cell: props => numeral(props.value).format(Formatters.DOLLARS_DECIMALS),
      Footer: numeral(sumReturned).format(Formatters.DOLLARS_DECIMALS),
    },
  ];
};

const PrivatePlacements = ({ data, type }) => {
  const privatePlacementData = data.activeInvestmentsData.privatePlacementData;
  const totalInvested = numeral(privatePlacementData.summary.items.totalInvested.value)
    .format(Formatters.DOLLARS_DECIMALS);
  const totalDistributions = numeral(privatePlacementData.summary.items.totalDistributions.value)
    .format(Formatters.DOLLARS_DECIMALS);
  const noPrivatePlacementText = data.noPrivatePlacementText;
  const noPrivatePlacementButtonUrl = data.noPrivatePlacementButtonURL;
  const noPrivatePlacementButtonText = data.noPrivatePlacementButtonContent;
  const columnsWide = (type === 'active') ? columnsActive(privatePlacementData) : columnsCompleted(privatePlacementData);
  const sectionTitle = (type === 'active') ? 'Active Investments' : 'Completed Investments';
  let allocation = 0;
  if (privatePlacementData.summary !== undefined) {
    allocation = privatePlacementData.summary.items.portfolioAllocation.value;
  }

  return privatePlacementData === {} ? '' : (
    <ExtendedFlex
      flexDirection="column"
      width={1}
      alignItems="center"
    >
      <DashboardSection
        totalInvested={totalInvested}
        totalDistributions={totalDistributions}
        title="PRIVATE PLACEMENTS"
        contentWidth="100%"
        allocation={allocation}
      >
        <ExtendedFlex
          flexDirection="column"
          width={1}
          alignItems="left"
          my={[30, 80]}
        >
          {
            (privatePlacementData.ios.length === 0) &&
            <Body
              type="B1"
              my={[0, 5]}
              ml="5%"
            >
              {noPrivatePlacementText}
            </Body>
          }
          {
            (privatePlacementData.ios.length === 0) &&
            <Link
              type="L1"
              mt={50}
              href={noPrivatePlacementButtonUrl}
              ml="5%"
            >
              {noPrivatePlacementButtonText}
            </Link>
          }
          {
            (privatePlacementData.ios.length !== 0) &&
            <ExtendedBox
              mx={[-50, -50, -50, 0, 0]}
            >
              <StyledTable
                title={sectionTitle}
                data={privatePlacementData.ios}
                columns={columnsWide}
                mx={0}
              />
            </ExtendedBox>
          }
        </ExtendedFlex>
      </DashboardSection>
    </ExtendedFlex>
  );
};

PrivatePlacements.propTypes = {
  data: PropTypes.object,
  type: PropTypes.string,
};

export default PrivatePlacements;
